// src/app/contexts/PermissionContext.tsx
//
// Contexto global de permissões do usuário autenticado.
// Busca GET /auth/me/permissions/ uma vez por sessão e distribui via Context.
// Todos os guards de rota e itens de sidebar consomem este contexto.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';

export interface SectorInfo {
  id: string;
  name: string;
  slug: string;
  color: string;
}

export interface UserPermissions {
  role: 'manager' | 'coordinator' | 'operator' | 'end_user';
  sectors: SectorInfo[];
  allowed_modules: string[];
  allowed_boards: string[];
  can_edit_school_profile: boolean;
  is_manager: boolean;
}

interface PermissionContextValue {
  permissions: UserPermissions | null;
  loading: boolean;
  error: boolean;
  refresh: () => void;
}

const PermissionContext = createContext<PermissionContextValue>({
  permissions: null,
  loading: true,
  error: false,
  refresh: () => {},
});

export function PermissionProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(false);

  const fetchPermissions = async () => {
    if (!isAuthenticated) {
      setPermissions(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const data = await api<UserPermissions>('/auth/me/permissions/');
      setPermissions(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Só busca permissões depois que o AuthContext confirma se há sessão.
  // Sem isso, o fetch dispara em rotas públicas (/login, /invite) sem token,
  // recebe 401 e o cliente HTTP força window.location.href = '/login'
  // (src/app/lib/api.ts) — na própria /login isso causa reload em loop.
  useEffect(() => {
    if (!authLoading) {
      fetchPermissions();
    }
  }, [authLoading, isAuthenticated]);

  return (
    <PermissionContext.Provider value={{ permissions, loading, error, refresh: fetchPermissions }}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionContext);
}

export function useHasModule(moduleSlug: string): boolean {
  const { permissions } = usePermissions();
  if (!permissions) return false;
  return permissions.allowed_modules.includes(moduleSlug);
}

export function useIsManager(): boolean {
  const { permissions } = usePermissions();
  return permissions?.is_manager ?? false;
}

export function useCanEditSchool(): boolean {
  const { permissions } = usePermissions();
  return permissions?.can_edit_school_profile ?? false;
}
